import React, { useMemo } from 'react';
import {
  Box,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';

import { useVideoCatalog } from '../../../queries';
import type { VideoOperationType } from '../../../domain';
import type { VideoControls } from './buildRequests';
import { estimateCost } from './buildRequests';
import { formatEstimate } from '../../../credits/naira';
import { useNairaRate } from '../../../credits/useNairaRate';
import { ModelTierPicker, resolvePricedModel } from './ModelTierPicker';

const DURATIONS = [4, 6, 8];
const ASPECTS = ['16:9', '9:16', '1:1'];
const RESOLUTIONS = ['720p', '1080p'];

export interface MotionControlsProps {
  kind: VideoOperationType;
  value: VideoControls;
  onChange: (next: VideoControls) => void;
}

/**
 * The cost-bearing motion knobs shared by every panel (A-F5): tier/model
 * picker + duration/aspect/resolution/audio + a live pre-submit cost estimate.
 * The estimate prices against the resolved model from the BE catalog so the
 * cost is legible to our cost-sensitive users (product spec §1.2).
 */
export const MotionControls: React.FC<MotionControlsProps> = ({ kind, value, onChange }) => {
  const { data } = useVideoCatalog();
  const models = useMemo(
    () => (data?.video ?? []).filter(p => p.available).flatMap(p => p.models),
    [data],
  );

  const priced = useMemo(
    () => resolvePricedModel(models, { modelTier: value.modelTier, model: value.model }, kind),
    [models, value.modelTier, value.model, kind],
  );
  const cost = estimateCost(value.durationSeconds, priced?.pricePerSecond ?? undefined);
  // What the wallet takes for it, beside the dollars (₦0 on the caller's own key).
  const nairaRate = useNairaRate('video', priced?.id);
  const maxDuration = priced?.maxDuration ?? 8;

  const set = <K extends keyof VideoControls>(key: K, v: VideoControls[K]) =>
    onChange({ ...value, [key]: v });

  return (
    <Stack spacing={1.5}>
      <ModelTierPicker
        kind={kind}
        durationSeconds={value.durationSeconds}
        value={{ modelTier: value.modelTier, model: value.model }}
        onChange={next => onChange({ ...value, modelTier: next.modelTier, model: next.model })}
        nairaRate={nairaRate}
      />

      <Stack direction="row" spacing={1}>
        <TextField
          select
          fullWidth
          size="small"
          label="Duration"
          value={value.durationSeconds}
          onChange={e => set('durationSeconds', Number(e.target.value))}
        >
          {DURATIONS.map(d => (
            <MenuItem key={d} value={d} disabled={d > maxDuration}>
              {d}s{d > maxDuration ? ' (over model max)' : ''}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="Aspect"
          value={value.aspectRatio}
          onChange={e => set('aspectRatio', e.target.value)}
        >
          {ASPECTS.map(a => (
            <MenuItem key={a} value={a}>
              {a}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          fullWidth
          size="small"
          label="Resolution"
          value={value.resolution}
          onChange={e => set('resolution', e.target.value)}
        >
          {RESOLUTIONS.map(r => (
            <MenuItem key={r} value={r}>
              {r}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={value.generateAudio}
            onChange={e => set('generateAudio', e.target.checked)}
          />
        }
        label={<Typography variant="body2">Generate audio</Typography>}
      />

      <Box>
        <Typography variant="caption" color="text.secondary" data-testid="cost-estimate">
          {cost != null
            && formatEstimate(cost, nairaRate)
            ? `${formatEstimate(cost, nairaRate)} · ${value.durationSeconds}s @ ${value.resolution}`
            : `${value.durationSeconds}s @ ${value.resolution}`}
        </Typography>
      </Box>
    </Stack>
  );
};
